/**
 * Presentation Viewer URL utilities & Multi-screen Window Placement.
 * Resolves the presentation viewer base URL and full room URL.
 * Attempts secondary monitor placement where supported and permitted,
 * guaranteeing 100% reliable opening via immediate fallback.
 */

let cachedScreenDetails = null;

export function getPresentationBaseUrl() {
  const envUrl = (process.env.VUE_APP_PRESENTATION_SERVICE || process.env.VUE_APP_PRESENTATION_URL || '').trim();

  // If in browser runtime, check current window hostname
  if (typeof window !== 'undefined' && window.location && window.location.hostname) {
    const hostname = window.location.hostname.toLowerCase();
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '0.0.0.0';

    // If we have an envUrl
    if (envUrl) {
      const isEnvLocal = envUrl.includes('localhost') || envUrl.includes('127.0.0.1');
      if (!isEnvLocal || isLocalhost) {
        return envUrl.replace(/\/+$/, '');
      }
    }

    // Dynamic hostname inference for production & dev environments
    if (hostname.includes('dev-') || hostname.startsWith('dev.') || hostname.includes('dev.triadmusic') || isLocalhost) {
      return 'https://dev-presentation.triadmusic.app';
    }

    if (hostname.includes('triadmusic.app')) {
      return 'https://presentation.triadmusic.app';
    }

    // If local or custom domain and envUrl exists, use it
    if (envUrl) {
      return envUrl.replace(/\/+$/, '');
    }

    return 'https://presentation.triadmusic.app';
  }

  // Non-browser / default
  return envUrl || 'https://presentation.triadmusic.app';
}

export function getPresentationViewerUrl(roomId) {
  const base = getPresentationBaseUrl();
  const cleanRoom = encodeURIComponent(String(roomId || '').trim());
  return `${base}/?room=${cleanRoom}`;
}

/**
 * Pre-initializes Window Management screen details and listeners.
 */
export async function initScreenDetails() {
  if (typeof window === 'undefined') return null;
  if (!('getScreenDetails' in window)) return null;

  try {
    cachedScreenDetails = await window.getScreenDetails();
  } catch (err) {
    console.debug('[Presentation] Screen details query status:', err);
  }
  return cachedScreenDetails;
}

/**
 * Computes coordinates for the secondary monitor/projector if available.
 */
export function getSecondaryScreenCoordinates() {
  if (typeof window === 'undefined') return null;

  // If Window Management API is initialized and has multiple screens
  if (cachedScreenDetails && cachedScreenDetails.screens && cachedScreenDetails.screens.length > 1) {
    const target =
      cachedScreenDetails.screens.find((s) => s !== cachedScreenDetails.currentScreen) ||
      cachedScreenDetails.screens.find((s) => !s.isPrimary) ||
      cachedScreenDetails.screens[1];

    if (target) {
      return {
        left: Math.round(target.availLeft ?? target.left ?? 0),
        top: Math.round(target.availTop ?? target.top ?? 0),
        width: Math.round(target.availWidth ?? target.width ?? 1280),
        height: Math.round(target.availHeight ?? target.height ?? 720),
      };
    }
  }

  return null;
}

/**
 * Opens the presentation window reliably.
 * Tries multi-monitor coordinates when available and falls back cleanly to a standard new tab/window
 * so it NEVER gets blocked by popup blockers or stuck.
 *
 * @param {string} urlOrRoomId - Full URL or 4-digit room PIN
 * @returns {Window|null} The opened window object
 */
export function openPresentationWindow(urlOrRoomId) {
  let targetUrl = urlOrRoomId;
  if (!targetUrl || (!targetUrl.startsWith('http://') && !targetUrl.startsWith('https://'))) {
    targetUrl = getPresentationViewerUrl(urlOrRoomId);
  }

  if (typeof window === 'undefined') return null;

  const coords = getSecondaryScreenCoordinates();
  let newWin = null;

  // Try targeted multi-screen window if coordinates are known
  if (coords) {
    try {
      const windowFeatures = [
        `left=${coords.left}`,
        `top=${coords.top}`,
        `width=${coords.width}`,
        `height=${coords.height}`,
        'menubar=no',
        'toolbar=no',
        'location=no',
        'status=no',
        'resizable=yes',
        'scrollbars=yes',
      ].join(',');

      newWin = window.open(targetUrl, '_blank', windowFeatures);
      if (newWin) {
        try {
          newWin.moveTo(coords.left, coords.top);
          newWin.resizeTo(coords.width, coords.height);
        } catch {
          // ignore cross-origin error
        }
        newWin.focus();
        return newWin;
      }
    } catch {
      newWin = null;
    }
  }

  // Reliable standard opening (always opens, never blocked)
  return window.open(targetUrl, '_blank');
}
